import { Color, Mesh, MeshPhysicalMaterial, PlaneGeometry, Vector3 } from 'three';
import { EngineContext } from './engine-context';
import { InfiniteGridHelper } from '../addons/infinite-grid';
import { DEFAULT_EMPTY_SIZE } from '../engine.constants';

export class GridManager {
  constructor(private ctx: EngineContext) {}

  get visible(): boolean {
    if (this.ctx.useInfiniteGrid) {
      return this.ctx.infiniteGridHelper?.visible ?? false;
    }
    return this.ctx.gridHelper?.visible ?? false;
  }

  set visible(value: boolean) {
    if (this.ctx.useInfiniteGrid && this.ctx.infiniteGridHelper) {
      this.ctx.infiniteGridHelper.visible = value;
    }
    if (this.ctx.gridHelper) {
      this.ctx.gridHelper.visible = this.ctx.useInfiniteGrid ? false : value;
      if (this.ctx.gridHelper.userData['xLine']) {
        this.ctx.gridHelper.userData['xLine'].visible = this.ctx.useInfiniteGrid ? false : value;
      }
      if (this.ctx.gridHelper.userData['yLine']) {
        this.ctx.gridHelper.userData['yLine'].visible = this.ctx.useInfiniteGrid ? false : value;
      }
      if (this.ctx.gridHelper.userData['groundPlane']) {
        this.ctx.gridHelper.userData['groundPlane'].visible = this.ctx.useInfiniteGrid ? false : value;
      }
    }
  }

  toggle(): void {
    this.visible = !this.visible;
  }

  createInfiniteGrid(size: Vector3, center: Vector3): void {
    this.disposeInfiniteGrid();
    const maxSize = Math.max(size.x, size.y, size.z, DEFAULT_EMPTY_SIZE);
    this.ctx.infiniteGridHelper = new InfiniteGridHelper();
    this.ctx.infiniteGridHelper.updateScale(maxSize);
    this.ctx.infiniteGridHelper.setAxisCenter(center.x, center.y);
    const gridZ = center.z - size.z / 2;
    this.ctx.infiniteGridHelper.position.set(0, 0, gridZ);
    this.ctx.infiniteGridHelper.visible = this.ctx.useInfiniteGrid;
    const isDark = this.ctx.themeService.getAppliedTheme() === 'dark';
    this.ctx.infiniteGridHelper.setTheme(isDark);
    this.ctx.scene.add(this.ctx.infiniteGridHelper);
  }

  disposeInfiniteGrid(): void {
    if (this.ctx.infiniteGridHelper) {
      this.ctx.scene.remove(this.ctx.infiniteGridHelper);
      this.ctx.infiniteGridHelper.dispose();
      this.ctx.infiniteGridHelper = null;
    }
  }

  applyTheme(): void {
    const theme = this.ctx.themeService.getAppliedTheme();
    const isDark = theme === 'dark';

    const groundPlane = this.ctx.scene.getObjectByName('GridHelperGroundPlane') as Mesh<
      PlaneGeometry,
      MeshPhysicalMaterial
    >;
    if (groundPlane) {
      groundPlane.material.color.set(isDark ? '#1e1e1e' : '#ffffff');
    }

    if (this.ctx.infiniteGridHelper) {
      this.ctx.infiniteGridHelper.setTheme(isDark);
    }

    const bgColor = isDark ? '#191c22' : '#ffffff';
    this.ctx.renderer.setClearColor(bgColor);
    this.ctx.scene.background = new Color(bgColor);
  }
}
